import { OnboardingHighlightState } from './onboarding-highlight-state.interface';
import classNames from 'classnames';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { CircleHelp, Clock3, Coins, Star } from 'lucide-react';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import { SignedTileDto } from 'shared/dto/project/signed-tile.dto';
import { DealViewSource } from 'shared/enums/deal/deal-view-source.enum';
import { GameViewSource } from 'shared/enums/game/game-view-source.enum';
import { PerformanceSetting } from 'shared/enums/performance-setting/performance-setting.enum';
import { TileClickResponseDto } from 'shared/enums/project/tile-click-response.dto';
import { TileClickStatus } from 'shared/enums/project/tile-click-status.enum';
import { ProfileSurveyExitModalType } from 'shared/enums/redirect/profile-survey-exit-modal-type.enum';
import { ProjectExitModalType } from 'shared/enums/redirect/project-exit-modal-type.enum';
import { UserActivityType } from 'shared/enums/user-activity-type/user-activity-type.enums';
import { ApprovedGameGoals } from 'shared/interfaces/approved-game-goals/approved-game-goals.interface';
import { ViewDealRequestDto } from 'shared/interfaces/deal/view-deal-request.dto';
import { GetDynamicProfilerQuestionResponseDto } from 'shared/interfaces/dynamic-profiler/get-dynamic-profiler-question-response.dto';
import { ViewGameRequestDto } from 'shared/interfaces/game/view-game-request.dto';
import { LevelProgressProps } from 'shared/interfaces/level-progress/level-progress.interface';
import { ProfileSurveyExitResult } from 'shared/interfaces/redirect/profile-survey-exit-result.interface';
import { ProjectExitResult } from 'shared/interfaces/redirect/project-exit-result.interface';
import { RewardProgressProps } from 'shared/interfaces/reward-progress/reward-progress.interface';
import { StreakProgressProps } from 'shared/interfaces/streak-progress/streak-progress.interface';
import { UserSettingType } from 'shared/interfaces/user-settings/user-setting-type.enum';
import { click } from 'src/api/click/click';
import { dynamicProfilerApi } from 'src/api/dynamic-profiler/dynamic-profiler';
import { gameRecommendationEngine } from 'src/api/game-recommendation-engine/game-recommendation-engine';
import { ipTracking } from 'src/api/ip-tracking/ip-tracking';
import { performanceSettingsApi } from 'src/api/performance-settings/performance-settings';
import { profileSurveyClick } from 'src/api/profile-survey/click/profile-survey-click';
import { profileSurveyApi } from 'src/api/profile-survey/profile-survey';
import { project } from 'src/api/project/project';
import { recommendationEngine } from 'src/api/recommendation-engine/recommendation-engine';
import { trustpilotInvite } from 'src/api/trustpilot-invite/trustpilot-invite';
import { userWithdrawalOption } from 'src/api/user-withdrawal-option/user-withdrawal-option';
import { user } from 'src/api/user/user';
import WelcomeAboard from 'src/components/CustomOverlay/WelcomeAboard/WelcomeAboard';
import DashboardCurrentQuests from 'src/components/DashboardCurrentQuests/DashboardCurrentQuests';
import DashboardPwaInstallCard from 'src/components/DashboardPwaInstallCard/DashboardPwaInstallCard';
import type { DynamicProfilerQuestion } from 'src/components/DynamicProfiler/dynamic-profiler-question.interface';
import { GameCardDataProps } from 'src/components/GameCard/GameCard';
import GamesCarousel from 'src/components/GamesCarousel/GamesCarousel';
import GamificationTopbar from 'src/components/GamificationTopbar/GamificationTopbar';
import { HomeOffersSkeleton, HomeSurveysSkeleton } from 'src/components/HomeDashboardSkeletons/HomeDashboardSkeletons';
import NoProjectsAvailablePage from 'src/components/NoProjectsAvailablePage/NoProjectsAvailablePage';
import PinnedJourneyCard from 'src/components/PinnedJourneyCard/PinnedJourneyCard';
import Sidebar from 'src/components/Sidebar/Sidebar';
import StaleProjectsPage from 'src/components/StaleProjectsPage/StaleProjectsPage';
import SurveyCard from 'src/components/SurveyCard/SurveyCard';
import { SurveyCardCoins } from 'src/components/SurveyCard/survey-card-coins.enum';
import { SurveyCardDataProps } from 'src/components/SurveyCard/survey-card-data-props.interface';
import { SurveyCardState } from 'src/components/SurveyCard/survey-card-state-enum';
import SurveysCarousel from 'src/components/SurveysCarousel/SurveysCarousel';
import TabBar from 'src/components/TabBar/TabBar';
import { ToastType } from 'src/components/Toast/toast-type.enum';
import TodayPlan from 'src/components/TodayPlan/TodayPlan';
import UserExperienceFeedback from 'src/components/UserExperienceFeedback/UserExperienceFeedback';
import { scheduleInviteMenuContentLoaded } from 'src/constants/invite-notifications';
import { useModal } from 'src/contexts/ModalContext';
import { useRewardWithdrawalContext } from 'src/contexts/RewardWithdrawalContext/RewardWithdrawalContext';
import { useUserAccountDetailsContext } from 'src/contexts/UserAccountDetailsContext/UserAccountDetailsContext';
import { useUserGamificationDetailsContext } from 'src/contexts/UserGamificationDetailsContext/UserGamificationDetailsContext';
import { useUserSettingsContext } from 'src/contexts/UserSettingsContext/UserSettingsContext';
import { useApprovedGameGoals } from 'src/hooks/use-approved-game-goals';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useGamesPlatformPreference } from 'src/hooks/use-games-platform-preference';
import { useGamificationStateRefresh } from 'src/hooks/use-gamification-state-refresh';
import { useQuestProgressNotification } from 'src/hooks/use-quest-progress-notification';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';
import { useUserCurrency } from 'src/hooks/use-user-currency';
import { useVerisoul } from 'src/hooks/use-verisoul';

import Button from 'src/components/Button/Button';
import { ButtonSize } from 'src/components/Button/button-size.enum';
import { ButtonState } from 'src/components/Button/button-state.enum';
import { ButtonType } from 'src/components/Button/button-type.enum';
import { interleaveGameAndDealOffers, sortOffersByPointsDescending } from 'src/utils/combine-game-offers';
import {
  pushFirstProjectCompleteConversionEvents,
  pushSecondProjectCompleteConversionEvents,
  pushSurveyCompletedEvents,
  pushToDataLayer,
} from 'src/utils/gtm';
import { GtmEventName } from 'src/utils/gtm-event-name.enum';
import { convertLocalCurrencyToPoints } from 'src/utils/local-currency';
import { mapDealTileResponseToGameCardData } from 'src/utils/mappers/deal-tile-response-to-game-card-data.mapper';
import { mapGameTileResponseToGameCardData } from 'src/utils/mappers/game-tile-response-to-game-card-data.mapper';
import { mapSignedTileToSurveyCardData } from 'src/utils/mappers/signed-tile-to-survey-card-data.mapper';
import { checkIfRewardProgressIsComplete } from 'src/utils/reward-progress-check';
import { getStreakStatusIcon } from 'src/utils/streak-status-icon.util';
import { showToast } from 'src/utils/toast-utils';

const DealView = lazy(() => import('src/components/DealView/DealView'));
const DynamicProfiler = lazy(() => import('src/components/DynamicProfiler/DynamicProfiler'));
const GameView = lazy(() => import('src/components/GameView/GameView'));
const BadQuality = lazy(() => import('src/components/CustomOverlay/BadQuality/BadQuality'));
const AyetSurveyStatus = lazy(() => import('src/components/CustomOverlay/AyetSurveyStatus/AyetSurveyStatus'));
const Blocker = lazy(() => import('src/components/CustomOverlay/Blocker/Blocker'));
const GameGoalsAchieved = lazy(() => import('src/components/CustomOverlay/GameGoalsAchieved/GameGoalsAchieved'));
const LevelOverlay = lazy(() => import('src/components/CustomOverlay/Level/Level'));
const LevelUp = lazy(() => import('src/components/CustomOverlay/LevelUp/LevelUp'));
const MidProjectIssue = lazy(() => import('src/components/CustomOverlay/MidProjectIssue/MidProjectIssue'));
const NewLevelUpIntro = lazy(() => import('src/components/CustomOverlay/NewLevelUpIntro/NewLevelUpIntro'));
const NewStreakIntro = lazy(() => import('src/components/CustomOverlay/NewStreakIntro/NewStreakIntro'));
const NotQualified = lazy(() => import('src/components/CustomOverlay/NotQualified/NotQualified'));
const OverQuota = lazy(() => import('src/components/CustomOverlay/OverQuota/OverQuota'));
const PointsOverlay = lazy(() => import('src/components/CustomOverlay/Points/Points'));
const ProjectComplete = lazy(() => import('src/components/CustomOverlay/ProjectComplete/ProjectComplete'));
const RewardUnlocked = lazy(() => import('src/components/CustomOverlay/RewardUnlocked/RewardUnlocked'));
const StreakOverlay = lazy(() => import('src/components/CustomOverlay/Streak/Streak'));
const StreakFreezeModal = lazy(() => import('src/components/CustomOverlay/StreakFreezeFound/StreakFreezeFound'));
const StreakFreezeUnlocked = lazy(
  () => import('src/components/CustomOverlay/StreakFreezeUnlocked/StreakFreezeUnlocked')
);
const StreakModal = lazy(() => import('src/components/CustomOverlay/StreakModal/StreakModal'));
const SurveyEntry = lazy(() => import('src/components/CustomOverlay/SurveyEntry/SurveyEntry'));
const SurveyIntro = lazy(() => import('src/components/CustomOverlay/SurveyIntro/SurveyIntro'));
const SurveyTips = lazy(() => import('src/components/CustomOverlay/SurveyTips/SurveyTips'));
const TrustpilotInvite = lazy(() =>
  import('src/components/CustomOverlay/TrustpilotInvite/TrustpilotInvite').then((module) => ({
    default: module.TrustpilotInvite,
  }))
);
const UserRating = lazy(() => import('src/components/CustomOverlay/UserRating/UserRating'));

dayjs.extend(timezone);

type SurveySortMode = 'fastest' | 'points' | 'best';

const DEFAULT_SURVEY_SORT_MODE: SurveySortMode = 'best';

interface HomeProps {
  surveysOnly?: boolean;
}

const Home: React.FC<HomeProps> = ({ surveysOnly = false }) => {
  const t = useTranslationByKey('PAGES.HOME');
  const tNavigation = useTranslationByKey('COMPONENTS.TAB_BAR');
  const tDynamicProfiler = useTranslationByKey('COMPONENTS.DYNAMIC_PROFILER');
  const { accountDetails, refreshAccountDetails } = useUserAccountDetailsContext();
  const { formatCurrency, formatPoints } = useUserCurrency();
  const notifyQuestProgress = useQuestProgressNotification();
  const { reward, isRewardLoaded } = useUserGamificationDetailsContext();
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [selectedTile, setSelectedTile] = useState<SignedTileDto | null>(null);
  const [isSurveyIntroOpen, setIsSurveyIntroOpen] = useState(false);
  const [tileClickResponseDto, setTileClickResponseDto] = useState<TileClickResponseDto | null>(null);
  const [isDynamicProfilerOpen, setIsDynamicProfilerOpen] = useState(false);
  const [dynamicProfilerQuestions, setDynamicProfilerQuestions] = useState<DynamicProfilerQuestion[]>([]);
  const [dynamicProfilerId, setDynamicProfilerId] = useState<number>(0);
  const [isDynamicProfilerSkippable, setIsDynamicProfilerSkippable] = useState<boolean>(false);
  const [isDynamicProfilerDismissible, setIsDynamicProfilerDismissible] = useState<boolean>(true);
  const [projects, setProjects] = useState<SurveyCardDataProps[]>([]);
  const [dashboardGames, setDashboardGames] = useState<GameCardDataProps[]>([]);
  const [dashboardDeals, setDashboardDeals] = useState<GameCardDataProps[]>([]);
  const [selectedDashboardGameRequest, setSelectedDashboardGameRequest] = useState<ViewGameRequestDto | null>(null);
  const [selectedDashboardDealRequest, setSelectedDashboardDealRequest] = useState<ViewDealRequestDto | null>(null);
  const [selectedDashboardGame, setSelectedDashboardGame] = useState<GameCardDataProps | null>(null);
  const [selectedDashboardDeal, setSelectedDashboardDeal] = useState<GameCardDataProps | null>(null);
  const [selectedDashboardGameRank, setSelectedDashboardGameRank] = useState<number | null>(null);
  const [surveySortMode, setSurveySortMode] = useState<SurveySortMode>(DEFAULT_SURVEY_SORT_MODE);
  const [selectedSurveyPartner, setSelectedSurveyPartner] = useState<string>('');
  const [rewardProgress, setRewardProgress] = useState<RewardProgressProps | null>(null);
  const [levelProgress, setLevelProgress] = useState<LevelProgressProps | null>(null);
  const [streakProgress, setStreakProgress] = useState<StreakProgressProps | null>(null);
  const [tileDetailsDto, setTileDetailsDto] = useState<string | null>(null);
  const [tileDetailsSignature, setTileDetailsSignature] = useState<string | null>(null);
  const [IsUserInProject, setIsUserInProject] = useState(false);
  const [userRatingOverlayOpened, setUserRatingOverlayOpened] = useState(false);
  const [levelUpOverlayOpened, setLevelUpOverlayOpened] = useState(false);
  const [isNewLevelUpIntroOpen, setIsNewLevelUpIntroOpen] = useState(false);
  const [isStreakFreezeFoundOpen, setIsStreakFreezeFoundOpen] = useState(false);
  const [isStreakFreezeUnlockedOpen, setIsStreakFreezeUnlockedOpen] = useState(false);
  const [streakFreezesAwarded, setStreakFreezesAwarded] = useState(0);
  const [levelUpQueue, setLevelUpQueue] = useState<
    Array<{
      levelReached: number;
      pointsEarned: number;
      rewardProgress?: RewardProgressProps | null;
    }>
  >([]);
  const [currentLevelUpIndex, setCurrentLevelUpIndex] = useState(0);
  // [DELETE IN PROD-792]
  const [isNewStreakIntroOpen, setIsNewStreakIntroOpen] = useState(false);
  // [END OF DELETION]
  const [streakCompletedOverlayOpened, setStreakCompletedOverlayOpened] = useState(false);
  const [rewardUnlockedOverlayOpened, setRewardUnlockedOverlayOpened] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingDashboardOffers, setIsLoadingDashboardOffers] = useState(!surveysOnly);
  const [isStale, setIsStale] = useState(false);
  const [isProfileSurvey, setIsProfileSurvey] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isPartialReward, setIsPartialReward] = useState(false);
  const [projectExitResult, setProjectExitResult] = useState<{ projectExitResult: ProjectExitResult } | null>(null);
  const [profileSurveyExitResult, setProfileSurveyExitResult] = useState<{
    profileSurveyExitResult: ProfileSurveyExitResult;
  } | null>(null);
  const [firstTimeBonus, setFirstTimeBonus] = useState<number | null>(null);
  const [trustpilotInviteOpen, setTrustpilotInviteOpen] = useState<boolean>(false);
  const [trustpilotInviteUrl, setTrustpilotInviteUrl] = useState<string | null>(null);
  const [trustpilotInviteId, setTrustpilotInviteId] = useState<number | null>(null);
  const [isSurveyLoading, setIsSurveyLoading] = useState<boolean>(false);
  const [hasReview, setHasReview] = useState<boolean>(false);
  const { getSessionId, authenticateCurrentSession, reinitializeSession } = useVerisoul();

  // Overlay states for stats tiles
  const [isPointsOpen, setIsPointsOpen] = useState(false);
  const [isStreakOpen, setIsStreakOpen] = useState(false);
  const [isLevelOpen, setIsLevelOpen] = useState(false);
  const [isSurveyTipsOpen, setIsSurveyTipsOpen] = useState(false);
  const {
    osTypeId: gamesOsTypeId,
    setOsTypeId: setGamesOsTypeId,
    shouldShowPlatformPrompt,
  } = useGamesPlatformPreference();

  const { isSuccessfulWithdrawalOpen, rewardSelectionOpen, setRewardSelectionOpen } = useRewardWithdrawalContext();
  const rewardPointsCost =
    reward && accountDetails
      ? convertLocalCurrencyToPoints(reward.amount, reward.usdExchangeRate ?? accountDetails.usdExchangeRate)
      : 0;
  const streakStatusIcon = getStreakStatusIcon({
    currentStreak: accountDetails?.currentStreak,
    streakStartDate: accountDetails?.streakStartDate,
    bonusCycleDay: accountDetails?.bonusCycleDay,
    timezone: accountDetails?.timezone,
  });
  const gamificationTopbarProps = {
    showStats: true,
    stats: [
      {
        value: accountDetails?.currentStreak || 0,
        unit: t('DAYS'),
        icon: streakStatusIcon,
        onClick: () => setIsStreakOpen(true),
        isClickable: true,
      },
      {
        customLabel: reward ? `${formatCurrency(reward.amount, reward.currencyCode)} ${reward.name}` : undefined,
        value: '',
        onClick: reward ? () => setIsPointsOpen(true) : undefined,
        isClickable: !!reward,
      },
    ],
    showProgressRing: true,
    homeProgressRingProps: {
      currentValue: accountDetails?.currentXpBalance || 0,
      maxValue: accountDetails?.requiredXpForNextLevel || 100,
      label: String(accountDetails?.currentLevel || 0),
      subLabel: 'Level',
      onClick: () => setIsLevelOpen(true),
    },
    rewardProgressRingProps:
      reward && accountDetails
        ? {
            currentValue: accountDetails.currentPointsBalance || 0,
            maxValue: rewardPointsCost,
            label: formatPoints(accountDetails.currentPointsBalance || 0),
            onClick: () => setIsPointsOpen(true),
          }
        : accountDetails
          ? {
              currentValue: accountDetails.currentPointsBalance || 0,
              maxValue: 1000,
              label: formatPoints(accountDetails.currentPointsBalance || 0),
              onClick: () => setIsPointsOpen(true),
            }
          : undefined,
  };

  // [UPDATE IN PROD-790] -- remove setNotifyAboutNewLevelingUpSystem
  const {
    userSettings,
    userSettingsLoaded,
    setProfileSurveyCompletion,
    setFirstDynamicProfilerCompletion,
    refreshUserSettings,
    setNotifyAboutNewLevelingUpSystem,
    saveUserSettings,
  } = useUserSettingsContext();

  const stopReloadingRef = useRef(false);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const MAX_REFRESH_ATTEMPTS = 20;
  const DASHBOARD_REFRESH_INTERVAL = 90000; // 90 seconds
  const attemptsRef = useRef(0);
  const rewardSelectionPromptedRef = useRef(false);
  const basicQualificationPromptedRef = useRef(false);
  // Ref to track if initial load has already happened
  const initialLoadCompletedRef = useRef(false);
  const initialDashboardOffersLoadedRef = useRef(false);
  const onboardingDummyProjectsRef = useRef<SurveyCardDataProps[] | null>(null);
  const isOnboardingProjectsPreviewRef = useRef(true);
  const hasTrackedSurveyCompletionModalRef = useRef(false);
  const isBasicQualificationCompleted = userSettings?.firstDynamicProfilerCompletion === true;
  const isProfileSurveyCompleted = userSettings?.profileSurveyCompletion === true;
  const hasUserSettings = userSettings != null;

  const refreshProjects = useCallback(
    async (resetCounter = false) => {
      if (!isProfileSurveyCompleted || isOnboardingProjectsPreviewRef.current) {
        setIsLoadingProjects(false);
        if (resetCounter) {
          setIsInitialLoad(false);
        }
        return;
      }

      if (resetCounter) {
        // Only reset the counter for deliberate resets like manual refresh
        attemptsRef.current = 0;
        stopReloadingRef.current = false;
        setIsStale(false);
      }

      // If we've been told to stop reloading (e.g. API consistently returns no projects), exit.
      if (stopReloadingRef.current && !resetCounter) {
        // Only bail out if not a reset, as a reset should try again.
        setIsLoadingProjects(false);
        return;
      }

      try {
        const prioritizedProjects = await recommendationEngine.fetchPrioritizedProjects();

        if (prioritizedProjects.length === 0) {
          setProjects([]);
          if (!resetCounter) {
            // For interval calls, if empty, stop further auto-reloading.
            stopReloadingRef.current = true;
          }
        } else {
          setProjects(prioritizedProjects.map(mapSignedTileToSurveyCardData));
          if (resetCounter) {
            attemptsRef.current = 0; // Reset counter on successful manual refresh
          }
          stopReloadingRef.current = false; // Allow future reloads.
        }

        if (surveysOnly) {
          scheduleInviteMenuContentLoaded('projects');
        }
      } catch (error) {
        // On error, for interval calls, attemptsRef would have been incremented by the interval.
        // isStale will be set by interval logic if attemptsRef hits the limit.
        // For manual/reset calls, this error means the initial load failed.
        // We don't set stale here directly from a single failed manual load,
        // but rely on the interval to eventually mark stale if retries fail.
      } finally {
        setIsLoadingProjects(false);
        if (resetCounter) {
          setIsInitialLoad(false);
        }
      }
    },
    [isProfileSurveyCompleted, setIsInitialLoad, setIsLoadingProjects, setIsStale, surveysOnly]
  );

  // Manual reload triggered when the user clicks "refresh" on stale dashboard page or on the "no surveys available" page.
  const handleRefreshFromIdleTimer = useCallback(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    // Reset all the state needed for a fresh start
    stopReloadingRef.current = false;
    attemptsRef.current = 0;
    setIsInitialLoad(true); // Reset isInitialLoad to show loading screen on manual refresh
    setIsStale(false); // Clear stale state

    // Set loading true for immediate feedback. The effect will also set it,
    // but this makes the UI responsive slightly faster to the click.
    setIsLoadingProjects(true);

    // Manually call refreshProjects with reset
    void refreshProjects(true);
  }, [refreshProjects]); // Add refreshProjects as dependency

  // Reloads projects when the user starts working on a project.
  const handleRefreshFromProjectEntry = useCallback(() => {
    stopReloadingRef.current = false;
    attemptsRef.current = 0;

    setIsLoadingProjects(true);
    void refreshProjects(true); // Reset counter when entering a project
  }, [refreshProjects]);

  const openRewardSelectionGate = useCallback(() => {
    rewardSelectionPromptedRef.current = true;
    setIsPointsOpen(false);
    setIsStreakOpen(false);
    setIsLevelOpen(false);
    setRewardSelectionOpen(true);
  }, [setRewardSelectionOpen]);

  useEffect(() => {
    if (IsUserInProject && !tileClickResponseDto?.isProfileSurvey) {
      void handleRefreshFromProjectEntry();
    }
  }, [IsUserInProject, handleRefreshFromProjectEntry, tileClickResponseDto]);

  const [onboardingHighlightState, setOnboardingHighlightState] = useState<OnboardingHighlightState>({
    surveyCardHighlighted: false,
    balanceRewardHighlighted: false,
  });

  const { isOpen } = useModal();

  useEffect(() => {
    isOnboardingProjectsPreviewRef.current =
      userSettings?.firstDynamicProfilerCompletion !== true || userSettings?.profileSurveyCompletion !== true;
  }, [userSettings?.firstDynamicProfilerCompletion, userSettings?.profileSurveyCompletion]);

  const loadProjects = useCallback(async () => {
    // If already stale, don't try to load new projects automatically
    if (isStale) {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    // Increment the counter BEFORE clearing interval or making requests
    // This ensures we're counting each call to loadProjects
    attemptsRef.current += 1;

    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    // If we've reached max attempts, mark as stale and stop
    if (attemptsRef.current >= MAX_REFRESH_ATTEMPTS) {
      setIsStale(true);
      setIsLoadingProjects(false);
      return; // Don't even try to load
    }

    await refreshProjects(false); // Don't reset counter for automatic refreshes

    // Check if we became stale during the refresh - don't restart interval if we did
    if (isStale) {
      return;
    }

    if (isProfileSurveyCompleted && !stopReloadingRef.current) {
      refreshIntervalRef.current = setInterval(() => {
        void loadProjects();
      }, DASHBOARD_REFRESH_INTERVAL);
    } else {
      setIsLoadingProjects(false);
    }
  }, [isProfileSurveyCompleted, refreshProjects, isStale]);

  useEffect(() => {
    if (isProfileSurveyCompleted) {
      stopReloadingRef.current = false;
    }
  }, [isProfileSurveyCompleted]);

  // [DELETE IN PROD-790]
  useEffect(() => {
    if (userSettings?.notifyAboutNewLevelingUpSystem && userSettings?.newLevelingUpPointsRewarded) {
      const init = async () => {
        const rewardProgress = await userWithdrawalOption.getRewardProgress(
          userSettings.newLevelingUpPointsRewarded || 0
        );
        setRewardProgress(rewardProgress);
        setIsNewLevelUpIntroOpen(true);
      };
      void init();
    }
  }, [userSettings?.newLevelingUpPointsRewarded, userSettings?.notifyAboutNewLevelingUpSystem]);
  // [END OF DELETION]

  // [DELETE IN PROD-792]
  // When NotifyAboutNewStreakSystem is true: fetch streak progress (backend pays and sets flag to false), then open modal.
  useEffect(() => {
    const shouldShowLevelUp = userSettings?.notifyAboutNewLevelingUpSystem && userSettings?.newLevelingUpPointsRewarded;
    const canShowStreak = !isNewLevelUpIntroOpen && !levelUpOverlayOpened && !shouldShowLevelUp;

    if (userSettings?.notifyAboutNewStreakSystem && canShowStreak) {
      const loadAndShow = async () => {
        const progress: StreakProgressProps | null = await user.getStreakProgress();
        setStreakProgress(progress ?? null);
        setIsNewStreakIntroOpen(true);
        void refreshAccountDetails();
      };
      void loadAndShow();
    } else if (!userSettings?.notifyAboutNewStreakSystem) {
      setIsNewStreakIntroOpen(false);
    }
  }, [
    userSettings?.notifyAboutNewStreakSystem,
    userSettings?.notifyAboutNewLevelingUpSystem,
    userSettings?.newLevelingUpPointsRewarded,
    isNewLevelUpIntroOpen,
    levelUpOverlayOpened,
    refreshAccountDetails,
  ]);
  // [END OF DELETION]

  const location = useLocation();
  const navigate = useNavigate();
  // Games are available immediately after registration; qualification is requested from the first survey instead.
  const requiresBasicQualificationOnboarding = false;
  const refreshDashboardGames = useCallback(async () => {
    if (surveysOnly) {
      setIsLoadingDashboardOffers(false);
      return;
    }

    if (!userSettingsLoaded || !hasUserSettings) {
      return;
    }

    const isInitialDashboardOffersLoad = !initialDashboardOffersLoadedRef.current;
    if (isInitialDashboardOffersLoad) {
      setIsLoadingDashboardOffers(true);
    }

    try {
      const [recommendedResult, dealsResult] = await Promise.allSettled([
        gameRecommendationEngine.fetchPrioritizedGames({ osTypeId: gamesOsTypeId, surface: 'homepage' }),
        gameRecommendationEngine.fetchPrioritizedDeals({ osTypeId: gamesOsTypeId }),
      ]);

      if (recommendedResult.status === 'fulfilled') {
        setDashboardGames(recommendedResult.value.map(mapGameTileResponseToGameCardData));
      } else if (isInitialDashboardOffersLoad) {
        setDashboardGames([]);
      }
      if (dealsResult.status === 'fulfilled') {
        setDashboardDeals(dealsResult.value.map(mapDealTileResponseToGameCardData));
      } else if (isInitialDashboardOffersLoad) {
        setDashboardDeals([]);
      }
    } finally {
      if (isInitialDashboardOffersLoad) {
        initialDashboardOffersLoadedRef.current = true;
        setIsLoadingDashboardOffers(false);
      }
    }
  }, [gamesOsTypeId, hasUserSettings, surveysOnly, userSettingsLoaded]);

  useEffect(() => {
    void refreshDashboardGames();
  }, [refreshDashboardGames]);

  const handleApprovedGoalsPayoutComplete = useCallback(
    async (paidGoals: ApprovedGameGoals | null) => {
      notifyQuestProgress(paidGoals?.questProgress);
      await Promise.all([refreshAccountDetails(), refreshDashboardGames()]);
    },
    [notifyQuestProgress, refreshAccountDetails, refreshDashboardGames]
  );

  const { approvedGoals, isPayingGoals, payoutFailed, loadApprovedGoals, dismissApprovedGoals } = useApprovedGameGoals({
    onPayoutComplete: handleApprovedGoalsPayoutComplete,
    shouldTrackOfferGoalCompleted: accountDetails?.isGoogleAcquired,
  });

  useGamificationStateRefresh({
    enabled: true,
    refreshAccountDetails,
    refreshUserSettings,
    refetchApprovedGameGoals: loadApprovedGoals,
    shouldRefetchApprovedGoals: () => true,
  });

  const dashboardOffers = useMemo(
    () => sortOffersByPointsDescending(interleaveGameAndDealOffers(dashboardGames, dashboardDeals)),
    [dashboardDeals, dashboardGames]
  );

  const handleDashboardGameClick = useCallback(
    (game: GameCardDataProps, context: { isTopGame: boolean; topGameRank: number }) => {
      if (!game.signature || !game.gameTileDetailsDto) {
        navigate('/games');
        return;
      }

      setSelectedDashboardGame(game);
      setSelectedDashboardGameRank(context.isTopGame ? context.topGameRank : null);
      setSelectedDashboardGameRequest({
        gameTile: {
          payoutPoints: game.points,
          xp: game.xp,
          rating: game.rating,
          gameTileDetailsDto: game.gameTileDetailsDto,
        },
        signature: game.signature,
        game_view_source_id: GameViewSource.Dashboard,
      });
    },
    [navigate]
  );

  const handleDashboardDealClick = useCallback(
    (deal: GameCardDataProps) => {
      if (!isBasicQualificationCompleted || deal.dealId == null) {
        return;
      }

      setSelectedDashboardDeal(deal);
      setSelectedDashboardDealRequest({
        dealId: deal.dealId,
        deal_view_source_id: DealViewSource.Dashboard,
        points: deal.points,
        rating: deal.rating,
      });
    },
    [isBasicQualificationCompleted]
  );

  const handleDashboardOfferClick = useCallback(
    (offer: GameCardDataProps, context: { isTopGame: boolean; topGameRank: number }) => {
      if (offer.cardType === 'deal') {
        handleDashboardDealClick(offer);
        return;
      }

      handleDashboardGameClick(offer, context);
    },
    [handleDashboardDealClick, handleDashboardGameClick]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const areAllModalsClosed =
        !isSurveyIntroOpen &&
        !isDynamicProfilerOpen &&
        !levelUpOverlayOpened &&
        !isNewLevelUpIntroOpen &&
        // [DELETE IN PROD-792]
        !isNewStreakIntroOpen &&
        // [END OF DELETION]
        !rewardUnlockedOverlayOpened &&
        !streakCompletedOverlayOpened &&
        !userRatingOverlayOpened &&
        !activeModal;

      const shouldShowTrustpilot = localStorage.getItem('showTrustpilotModal') === 'true';

      if (
        userSettings?.trustpilotInviteEligible &&
        areAllModalsClosed &&
        !isSuccessfulWithdrawalOpen &&
        shouldShowTrustpilot
      ) {
        const recordTrustpilotInviteDisplayedAndOpenModal = async () => {
          const result = await trustpilotInvite.recordTrustpilotInviteDisplayed();
          setTrustpilotInviteUrl(result.url);
          setTrustpilotInviteId(result.id);
          // After showing the modal once, reset the flag
          localStorage.setItem('showTrustpilotModal', 'false');
          setTrustpilotInviteOpen(true);
        };
        void recordTrustpilotInviteDisplayedAndOpenModal();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    userSettings?.trustpilotInviteEligible,
    isSuccessfulWithdrawalOpen,
    isSurveyIntroOpen,
    isDynamicProfilerOpen,
    levelUpOverlayOpened,
    isNewLevelUpIntroOpen,
    // [DELETE IN PROD-792]
    isNewStreakIntroOpen,
    // [END OF DELETION]
    rewardUnlockedOverlayOpened,
    streakCompletedOverlayOpened,
    userRatingOverlayOpened,
    activeModal,
  ]);

  const { isMobile } = useBreakpoint();

  // This is a workaround to prevent the body from scrolling when the modal closes (which is a side effect of the Modal component)
  useEffect(() => {
    if (isDynamicProfilerOpen || isSurveyIntroOpen || isOpen || activeModal) {
      setTimeout(() => {
        document.body.style.overflow = 'hidden';
      }, 0);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [activeModal, isDynamicProfilerOpen, isOpen, isSurveyIntroOpen, onboardingHighlightState]);

  const requiresRewardSelectionOnboarding = !!userSettings?.welcomeModalAcknowledgement && isRewardLoaded && !reward;

  useEffect(() => {
    if (!requiresRewardSelectionOnboarding) {
      rewardSelectionPromptedRef.current = false;
      return;
    }

    if (rewardSelectionOpen) {
      rewardSelectionPromptedRef.current = true;
      return;
    }

    if (rewardSelectionPromptedRef.current || isOpen || activeModal || isSurveyIntroOpen || isDynamicProfilerOpen) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      openRewardSelectionGate();
    }, 180);

    return () => window.clearTimeout(timeoutId);
  }, [
    activeModal,
    isDynamicProfilerOpen,
    isOpen,
    isSurveyIntroOpen,
    openRewardSelectionGate,
    requiresRewardSelectionOnboarding,
    rewardSelectionOpen,
  ]);

  const clearLocationState = () => {
    navigate(location.pathname, { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const normalizeProjectExitResultWrapper = (
      value: { projectExitResult: ProjectExitResult } | ProjectExitResult
    ): { projectExitResult: ProjectExitResult } => {
      if ('projectExitModalType' in value) {
        return { projectExitResult: value };
      }

      return value;
    };

    const extractProjectExitResult = (): { projectExitResult: ProjectExitResult } | null => {
      const param = params.get('projectExitResult');

      if (param) {
        try {
          const parsed = JSON.parse(decodeURIComponent(param)) as
            | { projectExitResult: ProjectExitResult }
            | ProjectExitResult;
          window.history.replaceState({}, '', location.pathname);
          return normalizeProjectExitResultWrapper(parsed);
        } catch (error) {
          console.error('Error parsing projectExitResult from URL:', error);
        }
      }

      const state = location.state as {
        projectExitResult?: { projectExitResult: ProjectExitResult } | ProjectExitResult;
      } | null;
      const fromState = state?.projectExitResult;

      return fromState ? normalizeProjectExitResultWrapper(fromState) : null;
    };

    const resultWrapper = extractProjectExitResult();
    setProjectExitResult(resultWrapper);

    if (!resultWrapper) return;

    const result = resultWrapper.projectExitResult;

    const ipChanged = void ipTracking.detectIpChange();
    if (ipChanged) {
      void reinitializeSession();
    }

    if (result.projectExitModalType === ProjectExitModalType.ProjectCompleted) {
      void authenticateCurrentSession(UserActivityType.ProjectComplete);
    } else {
      void authenticateCurrentSession(UserActivityType.ProjectTermination);
    }

    if (result.rewardProgress) {
      setRewardProgress(result.rewardProgress);
    }
    if (result.levelProgress) {
      setLevelProgress(result.levelProgress);
    }
    if (result.streakProgress) {
      setStreakProgress(result.streakProgress);
    }
    setStreakFreezesAwarded(result.streakFreezeAwarded ? 1 : 0);
    notifyQuestProgress(result.questProgress);
    void refreshAccountDetails();
    if (result.tileDetailsDto) {
      setTileDetailsDto(result.tileDetailsDto);
    }
    if (result.tileDetailsSignature) {
      setTileDetailsSignature(result.tileDetailsSignature);
    }
    if (result.hasReview) {
      setHasReview(result.hasReview);
    }
    setIsPartialReward(result.isPartialReward ?? false);

    setActiveModal(result.projectExitModalType || null);

    if (result.levelProgress?.isUserLeveledUp && result.levelProgress?.startingLevel === 1) {
      pushToDataLayer(GtmEventName.Level1Reached);
    }

    clearLocationState();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.search]);

  useEffect(() => {
    // If the user has exited a profile survey - show the corresponding modal
    const extractProfileSurveyExitResult = (): ProfileSurveyExitResult | null => {
      const param = new URLSearchParams(location.search).get('profileSurveyExitResult');

      if (param) {
        try {
          const result = JSON.parse(decodeURIComponent(param)) as ProfileSurveyExitResult;
          window.history.replaceState({}, '', location.pathname);
          return result;
        } catch (error) {
          console.error('Error parsing profileSurveyExitResult from URL:', error);
        }
      }

      const locationState = location.state as { profileSurveyExitResult?: ProfileSurveyExitResult } | null;
      return locationState?.profileSurveyExitResult || null;
    };

    const profileSurveyExitResult = extractProfileSurveyExitResult();

    setProfileSurveyExitResult(profileSurveyExitResult ? { profileSurveyExitResult } : null);

    if (!profileSurveyExitResult) {
      return;
    }
    setIsProfileSurvey(true);
    setIsPartialReward(false);
    if (profileSurveyExitResult.rewardProgress) {
      setRewardProgress(profileSurveyExitResult.rewardProgress);
    }
    if (profileSurveyExitResult.levelProgress) {
      setLevelProgress(profileSurveyExitResult.levelProgress);
    }
    if (profileSurveyExitResult.streakProgress) {
      setStreakProgress(profileSurveyExitResult.streakProgress);
    }
    setStreakFreezesAwarded(profileSurveyExitResult.streakFreezeAwarded ? 1 : 0);
    void refreshAccountDetails();
    if (profileSurveyExitResult.tileDetailsDto) {
      setTileDetailsDto(profileSurveyExitResult.tileDetailsDto);
    }
    if (profileSurveyExitResult.tileDetailsSignature) {
      setTileDetailsSignature(profileSurveyExitResult.tileDetailsSignature);
    }
    setActiveModal(profileSurveyExitResult.profileSurveyExitModalType);

    if (
      profileSurveyExitResult.levelProgress?.isUserLeveledUp &&
      profileSurveyExitResult.levelProgress?.startingLevel === 1
    ) {
      pushToDataLayer(GtmEventName.Level1Reached);
    }

    // Clear location state to prevent showing modals again after page refresh
    clearLocationState();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.search]);

  useEffect(() => {
    if (activeModal !== ProjectExitModalType.ProjectCompleted) {
      hasTrackedSurveyCompletionModalRef.current = false;
      return;
    }

    if (hasTrackedSurveyCompletionModalRef.current) {
      return;
    }

    hasTrackedSurveyCompletionModalRef.current = true;

    if (isProfileSurvey || profileSurveyExitResult) {
      pushSurveyCompletedEvents();
      pushToDataLayer(GtmEventName.ProfileSurveyEnd);
      return;
    }

    const funnelEvents = projectExitResult?.projectExitResult.funnelEvents;
    if (funnelEvents?.justCompletedFirstProject) {
      pushFirstProjectCompleteConversionEvents();
    } else if (funnelEvents?.justCompletedSecondProject) {
      pushSecondProjectCompleteConversionEvents();
    } else {
      pushSurveyCompletedEvents();
    }
  }, [activeModal, isProfileSurvey, profileSurveyExitResult, projectExitResult]);

  useEffect(() => {
    if (!IsUserInProject || !tileDetailsDto || !tileDetailsSignature) {
      return;
    }

    const interval = setInterval(() => {
      void (async () => {
        const hasClickTerminated = tileClickResponseDto?.isProfileSurvey
          ? await profileSurveyApi.hasProfileSurveyCompleted()
          : await click.hasClickTerminated(tileDetailsDto, tileDetailsSignature);
        if (hasClickTerminated) {
          clearInterval(interval);
          window.location.reload();
        }
      })();
    }, 5000); // Polling for status every 5s
    return () => clearInterval(interval);
  }, [IsUserInProject, tileDetailsDto, tileDetailsSignature, tileClickResponseDto]);

  useEffect(() => {
    const setupProjectsAndInterval = async () => {
      if (!userSettingsLoaded) {
        return;
      }

      if (isStale) {
        setIsLoadingProjects(false);
        return;
      }

      if (attemptsRef.current >= MAX_REFRESH_ATTEMPTS && !isInitialLoad) {
        setIsStale(true);
        setIsLoadingProjects(false);
        return;
      }

      if (!isBasicQualificationCompleted && isProfileSurveyCompleted) {
        setIsLoadingProjects(true);
        try {
          let projectLimit = 8;
          let pointXpConversion = 0.1;

          try {
            const performanceSettings = await performanceSettingsApi.getPerformanceSettings();
            projectLimit = Number(performanceSettings[PerformanceSetting.HomepageProjectInvitesLimit]);
            pointXpConversion = Number(performanceSettings[PerformanceSetting.PointXpConversion]);
          } catch {
            // Fall back to defaults so preview tiles still render.
          }

          if (!onboardingDummyProjectsRef.current) {
            onboardingDummyProjectsRef.current = generateDummyProjects(projectLimit, pointXpConversion);
          }

          setProjects(onboardingDummyProjectsRef.current ?? []);
        } finally {
          setIsLoadingProjects(false);
          if (isInitialLoad) {
            setIsInitialLoad(false);
          }
        }
      } else if (!isProfileSurveyCompleted) {
        // Profile survey onboarding: show only the clickable profile survey tile.
        setIsLoadingProjects(true);
        try {
          const profileSurvey = await profileSurveyApi.getProfileSurveyByUserCountry();

          setProjects([
            {
              xpValue: profileSurvey.tile.xp,
              rating: profileSurvey.tile.rating,
              coinIcon: SurveyCardCoins.CoinBig,
              coinValue: profileSurvey.tile.payoutPoints,
              loiSeconds: profileSurvey.tile.loiSeconds,
              isProfileSurvey: true,
              encryptedDetails: profileSurvey.tile.tileDetailsDto || '',
              projectDetailsDto: profileSurvey.tile.projectDetailsDto,
              signature: profileSurvey.signature,
            } as SurveyCardDataProps,
          ]);
        } catch {
          setProjects([]);
        } finally {
          setIsLoadingProjects(false);
          if (isInitialLoad) {
            setIsInitialLoad(false);
          }
        }
      } else {
        // A manual refresh reuses the initial-load UI state to show the loader.
        // Let that request control loading instead of this effect immediately
        // flipping the page back to the stale / no-projects screen.
        if (initialLoadCompletedRef.current && isInitialLoad) {
          return;
        }

        if (!initialLoadCompletedRef.current) {
          initialLoadCompletedRef.current = true;
          setIsLoadingProjects(true);
          await refreshProjects(true);
        }

        if (!isStale && isProfileSurveyCompleted && !stopReloadingRef.current) {
          setIsLoadingProjects(false);
          refreshIntervalRef.current = setInterval(() => {
            void loadProjects();
          }, DASHBOARD_REFRESH_INTERVAL);
        } else {
          setIsLoadingProjects(false);
        }
      }
    };

    void setupProjectsAndInterval();

    return () => {
      // Also clear the global ref if it's still set (e.g. component unmounts while stale)
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [
    isBasicQualificationCompleted,
    isProfileSurveyCompleted,
    userSettingsLoaded,
    refreshProjects,
    loadProjects,
    isInitialLoad,
    isStale,
  ]);

  function generateDummyProjects(total: number, pointXpConversion: number) {
    const projects: SurveyCardDataProps[] = [];

    const randomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

    const countHigh = Math.floor(total * 0.15); // 200-250 points
    const countUpperMid = Math.floor(total * 0.15); // 100-150 points
    const countMid = Math.floor(total * 0.3); // 50-100 points
    const countLow = total - (countHigh + countUpperMid + countMid); // 10-50 points

    const guaranteedHighValueProject = randomInt(200, 250);

    const shuffledProjectsArray: number[] = [];

    for (let i = 0; i < countHigh - 1; i++) shuffledProjectsArray.push(randomInt(200, 250));

    for (let i = 0; i < countUpperMid; i++) shuffledProjectsArray.push(randomInt(100, 150));
    for (let i = 0; i < countMid; i++) shuffledProjectsArray.push(randomInt(50, 100));
    for (let i = 0; i < countLow; i++) shuffledProjectsArray.push(randomInt(10, 50));

    for (let i = shuffledProjectsArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledProjectsArray[i], shuffledProjectsArray[j]] = [shuffledProjectsArray[j], shuffledProjectsArray[i]];
    }

    const topPosition = Math.floor(Math.random() * Math.min(5, shuffledProjectsArray.length + 1));
    const pointsArray = [
      ...shuffledProjectsArray.slice(0, topPosition),
      guaranteedHighValueProject,
      ...shuffledProjectsArray.slice(topPosition),
    ];

    pointsArray.forEach((points) => {
      const xp = Math.round(points * pointXpConversion);
      const time = Math.max(1, Math.min(25, Math.floor(points / 10) + Math.floor(Math.random() * 5) - 2)) * 60;

      projects.push({
        xpValue: xp,
        rating: parseFloat((Math.random() * (5 - 3) + 3).toFixed(1)),
        coinIcon: SurveyCardCoins.CoinBig,
        coinValue: points,
        loiSeconds: time,
        isProfileSurvey: false,
      });
    });

    return projects;
  }

  const processDynamicProfilerCompletion = async (
    currentDtoFromCaller: string | null,
    currentSigFromCaller: string | null
  ) => {
    if (!selectedTile || !currentDtoFromCaller || !currentSigFromCaller) {
      return;
    }

    const dynamicProfilerCompletionPayload: SignedTileDto = {
      tile: {
        rating: selectedTile.tile.rating,
        payoutPoints: selectedTile.tile.payoutPoints,
        xp: selectedTile.tile.xp,
        loiSeconds: selectedTile.tile.loiSeconds,
        tileDetailsDto: currentDtoFromCaller,
      },
      signature: currentSigFromCaller,
    };

    try {
      const response = await dynamicProfilerApi.processDynamicProfilerCompletion(dynamicProfilerCompletionPayload);
      setTileClickResponseDto(response);

      if (response.tileDetailsDto && response.signature) {
        setTileDetailsDto(response.tileDetailsDto);
        setTileDetailsSignature(response.signature);
      }

      setActiveModal(response.tileClickStatus ?? null);

      // Remove the tile from projects when OverQuota or NotQualified
      if (
        response.tileClickStatus === TileClickStatus.OverQuota ||
        response.tileClickStatus === TileClickStatus.NotQualified
      ) {
        setProjects((prevProjects) =>
          prevProjects.filter((p) => p.encryptedDetails !== selectedTile.tile.tileDetailsDto)
        );
      }
    } catch (error) {
      setActiveModal(null);
      showToast(tDynamicProfiler('ERROR_SUBMITTING_RESULTS'), ToastType.Error);
    }
  };

  const handleSurveyCardClick = async (
    surveyCardDataProps: SurveyCardDataProps,
    isFirstDynamicProfilerFilled?: boolean
  ) => {
    // Set loading state to show overlay
    setIsSurveyLoading(true);

    // Reset the refresh attempts counter on any tile click
    attemptsRef.current = 0;

    let tileClick = null;

    // Reset the level, reward, and streak progress
    setHasReview(false);
    setLevelProgress(null);
    setRewardProgress(null);
    setStreakProgress(null);

    const verisoulSessionId = await getSessionId();

    if (surveyCardDataProps.isProfileSurvey) {
      setIsUserInProject(false);
      const selectedTile: SignedTileDto = {
        tile: {
          rating: surveyCardDataProps.rating,
          payoutPoints: surveyCardDataProps.coinValue,
          xp: surveyCardDataProps.xpValue,
          loiSeconds: surveyCardDataProps.loiSeconds,
          tileDetailsDto: surveyCardDataProps.encryptedDetails,
        },
        // Signature for the encrypted session uid value
        signature: surveyCardDataProps.signature || '',
      };

      setSelectedTile(selectedTile);

      const tileClickResponse = await profileSurveyClick.sendTileClick(selectedTile, verisoulSessionId ?? undefined);
      tileClickResponse.isProfileSurvey = true;

      setTileClickResponseDto(tileClickResponse);
      if (tileClickResponse && tileClickResponse?.tileDetailsDto && tileClickResponse?.signature) {
        setTileDetailsDto(tileClickResponse.tileDetailsDto);
        setTileDetailsSignature(tileClickResponse.signature);
      }
      tileClick = tileClickResponse;
    }
    if (tileClick?.tileClickStatus && tileClick.tileClickStatus !== TileClickStatus.Success) {
      setActiveModal(tileClick.tileClickStatus);
      if (tileClick.tileClickStatus === TileClickStatus.OverQuota) {
        setProfileSurveyCompletion(true);
        handleRefreshFromIdleTimer();
      }
    } else if (tileClick?.tileClickStatus && tileClick.tileClickStatus === TileClickStatus.Success) {
      if (!userSettings?.surveyIntroCompletion) {
        setIsSurveyIntroOpen(true);
      } else if (isFirstDynamicProfilerFilled === false) {
        await setupDynamicProfiler(false);
      } else if (!userSettings?.profileSurveyCompletion && surveyCardDataProps.isProfileSurvey && tileClick) {
        setActiveModal(tileClick.tileClickStatus);
      }
    }
    if (!surveyCardDataProps.isProfileSurvey) {
      const tileUserClickedOn: SignedTileDto = {
        tile: {
          rating: surveyCardDataProps.rating,
          payoutPoints: surveyCardDataProps.coinValue,
          xp: surveyCardDataProps.xpValue,
          loiSeconds: surveyCardDataProps.loiSeconds,
          tileDetailsDto: surveyCardDataProps.encryptedDetails || '',
        },
        // Signature for the encrypted value
        signature: surveyCardDataProps.signature || '',
      };

      setSelectedTile(tileUserClickedOn);

      const tileClickResponse = await project.sendTileClick(
        tileUserClickedOn,
        verisoulSessionId ?? undefined,
        reinitializeSession
      );
      // Set TileDetailsDto and Signature if available in the response *before* other state updates or modal activation
      if (tileClickResponse?.tileDetailsDto && tileClickResponse?.signature) {
        setTileDetailsDto(tileClickResponse.tileDetailsDto);
        setTileDetailsSignature(tileClickResponse.signature);
      }

      // Store the full response for other potential uses (like SurveyEntry modal or other logic)
      setTileClickResponseDto(tileClickResponse);

      if (tileClickResponse?.tileClickStatus) {
        if (tileClickResponse.tileClickStatus === TileClickStatus.DynamicProfiler) {
          // State for tileDetailsDto/Signature was updated above if present.
          const dynamicProfilerData = tileClickResponse.dynamicProfiler;
          const dynamicProfilerQuestions = dynamicProfilerData?.questions;

          if (dynamicProfilerData?.dynamicProfilerId) {
            const profileId = dynamicProfilerData.dynamicProfilerId;
            setDynamicProfilerId(Number(profileId));
          }

          if (dynamicProfilerQuestions && dynamicProfilerQuestions.length > 0) {
            await setupDynamicProfiler(true, dynamicProfilerQuestions, tileClickResponse);
          }
        } else {
          // For other statuses like OverQuota, NotQualified, etc.
          // State for tileDetailsDto/Signature was updated above if present.
          setActiveModal(tileClickResponse.tileClickStatus);

          // Remove the tile from projects when OverQuota or NotQualified
          if (
            tileClickResponse.tileClickStatus === TileClickStatus.OverQuota ||
            tileClickResponse.tileClickStatus === TileClickStatus.NotQualified
          ) {
            setProjects((prevProjects) =>
              prevProjects.filter((p) => p.encryptedDetails !== surveyCardDataProps.encryptedDetails)
            );
          }
        }
      }
    }
    // Clear loading state when survey click process is complete
    setIsSurveyLoading(false);
  };

  /**
   * A synchronous wrapper for the async handleSurveyCardClick function
   * This wrapper is used to avoid the eslint no-misused-promises error in onClick handlers
   */
  const handleSurveyCardClickSync = (item: SurveyCardDataProps, surveyIndex: number) => {
    if (requiresRewardSelectionOnboarding) {
      openRewardSelectionGate();
      return;
    }

    if (!isBasicQualificationCompleted) {
      if (surveyIndex === 0) {
        openBasicQualificationGate();
      }
      return;
    }

    if (isSurveyProfileGateActive && surveyIndex !== 0) {
      return;
    }

    void handleSurveyCardClick(item, userSettings?.firstDynamicProfilerCompletion ?? false);
  };

  const nextSurvey = useMemo(() => {
    if (isStale || isLoadingProjects || isInitialLoad) {
      return null;
    }

    const visibleSurveys = projects.filter(
      (survey) =>
        !survey.isProfileSurvey &&
        !!survey.encryptedDetails &&
        !!survey.signature &&
        survey.encryptedDetails !== selectedTile?.tile.tileDetailsDto
    );

    return visibleSurveys.reduce<SurveyCardDataProps | null>((highestScoringSurvey, survey) => {
      if (
        highestScoringSurvey === null ||
        (survey.score ?? Number.NEGATIVE_INFINITY) > (highestScoringSurvey.score ?? Number.NEGATIVE_INFINITY)
      ) {
        return survey;
      }

      return highestScoringSurvey;
    }, null);
  }, [isInitialLoad, isLoadingProjects, isStale, projects, selectedTile]);

  const handleTryNextSurvey = () => {
    if (!nextSurvey) {
      return;
    }

    setActiveModal(null);
    setIsUserInProject(false);
    setTileClickResponseDto(null);
    setUserRatingOverlayOpened(false);
    setIsProfileSurvey(false);
    setProjectExitResult(null);
    setProfileSurveyExitResult(null);
    setTileDetailsDto(null);
    setTileDetailsSignature(null);
    handleSurveyCardClickSync(nextSurvey, 0);
  };

  /**
   * Maps API question format to our internal format
   */
  const processQuestionsFormat = useCallback(
    (questions: GetDynamicProfilerQuestionResponseDto[]): DynamicProfilerQuestion[] => {
      return questions.map((question) => ({
        id: question.id,
        name: `question-${question.id}`,
        type: question.type,
        label: question.questionText,
        options: question.answers
          ? question.answers.map((answer) => ({
              label: answer.answerText,
              value: answer.id.toString(),
              answerCode: answer.answerCode,
            }))
          : [],
      }));
    },
    []
  );

  /**
   * Sets up the dynamic profiler with questions from either the tile click response or the API
   */
  const setupDynamicProfiler = useCallback(
    async (
      isSkippable = false,
      existingQuestions?: GetDynamicProfilerQuestionResponseDto[],
      currentTileClickResponse?: TileClickResponseDto | null,
      isDismissible = true
    ) => {
      setIsPointsOpen(false);
      setIsStreakOpen(false);
      setIsLevelOpen(false);
      setIsDynamicProfilerSkippable(isSkippable);
      setIsDynamicProfilerDismissible(isDismissible);
      setIsDynamicProfilerOpen(true);

      let questions;

      if (existingQuestions && existingQuestions.length > 0) {
        // Use the provided questions (from tile click response)
        questions = processQuestionsFormat(existingQuestions);
        setDynamicProfilerQuestions(questions);
        // Get the dynamicProfilerId from the most recent tileClickResponseDto or the passed current response
        const sourceResponse = currentTileClickResponse || tileClickResponseDto;

        if (sourceResponse) {
          let profileId = null;

          if (sourceResponse.dynamicProfiler && sourceResponse.dynamicProfiler?.dynamicProfilerId) {
            profileId = sourceResponse.dynamicProfiler.dynamicProfilerId;
          }

          if (profileId !== null && profileId !== 0) {
            setDynamicProfilerId(Number(profileId));
          }
        }
      } else {
        // Fetch first dynamic profiler questions from the API
        const dynamicProfiler = await dynamicProfilerApi.getFirstDynamicProfiler();
        questions = processQuestionsFormat(dynamicProfiler.questions);
        setDynamicProfilerQuestions(questions);
        setDynamicProfilerId(dynamicProfiler.dynamicProfilerId);
      }

      if (!questions || questions.length <= 0) {
        setIsDynamicProfilerOpen(false);
      }
    },
    [processQuestionsFormat, tileClickResponseDto]
  );

  const openBasicQualificationGate = useCallback(() => {
    basicQualificationPromptedRef.current = true;
    setIsPointsOpen(false);
    setIsStreakOpen(false);
    setIsLevelOpen(false);
    void setupDynamicProfiler(false);
  }, [setupDynamicProfiler]);

  const handleDashboardOfferClickDuringOnboarding = useCallback(
    (offer: GameCardDataProps, context: { isTopGame: boolean; topGameRank: number }) => {
      if (requiresRewardSelectionOnboarding) {
        openRewardSelectionGate();
        return;
      }

      handleDashboardOfferClick(offer, context);
    },
    [
      handleDashboardOfferClick,
      openBasicQualificationGate,
      openRewardSelectionGate,
      requiresBasicQualificationOnboarding,
      requiresRewardSelectionOnboarding,
    ]
  );

  const onSurveyIntroClose = () => {
    setIsSurveyIntroOpen(false);
  };

  const onSurveyIntroComplete = async () => {
    setIsSurveyIntroOpen(false);
    if (!userSettings?.firstDynamicProfilerCompletion) {
      await setupDynamicProfiler(false);
      return;
    }

    if (tileClickResponseDto?.tileClickStatus === TileClickStatus.Success) {
      setActiveModal(TileClickStatus.Success);
    }
  };

  // Synchronous wrappers for callbacks to pass linting errors
  const handleSurveyIntroCloseSync = () => {
    void onSurveyIntroClose();
  };

  const handleSurveyIntroCompleteSync = () => {
    void onSurveyIntroComplete();
  };

  // Create a synchronous wrapper for processDynamicProfilerCompletion to pass linting error
  const handleDynamicProfilerCompletionSync = () => {
    const isCompletingBasicQualification = !userSettings?.firstDynamicProfilerCompletion && !isDynamicProfilerSkippable;
    setIsDynamicProfilerOpen(false);

    if (isCompletingBasicQualification) {
      onboardingDummyProjectsRef.current = null;
      setActiveModal(null);
      setFirstDynamicProfilerCompletion(true);
      void refreshUserSettings();
      const profileSurveyProjectTile = projects.find((project) => project.isProfileSurvey);
      if (profileSurveyProjectTile) {
        void handleSurveyCardClick(profileSurveyProjectTile, true);
      }
      return;
    }

    void processDynamicProfilerCompletion(tileDetailsDto, tileDetailsSignature);
  };

  const handleMultipleLevelUps = (progress: LevelProgressProps | null = levelProgress) => {
    if (!progress?.isUserLeveledUp || !progress?.leveledUpCount || !progress?.startingLevel) {
      setLevelUpOverlayOpened(true);
      return;
    }

    if (progress.rewardProgress && progress.rewardProgress.length > 0) {
      setLevelUpQueue(
        progress.rewardProgress.map((rewardProgress, index) => ({
          levelReached: progress.startingLevel + index + 1,
          pointsEarned: rewardProgress.pointsEarned || 0,
          rewardProgress: rewardProgress,
        }))
      );
      setCurrentLevelUpIndex(0);
      setLevelUpOverlayOpened(true);
      return;
    }

    const levelsGained = progress.leveledUpCount;
    const startingLevel = progress.startingLevel;
    const totalPointsEarned = progress.pointsEarnedForEachLevelUp || [];

    const levelUpQueue = [];

    for (let i = 0; i < levelsGained; i++) {
      const currentLevel = startingLevel + i;
      const nextLevel = currentLevel + 1;

      levelUpQueue.push({
        levelReached: nextLevel,
        pointsEarned: totalPointsEarned[i],
        rewardProgress: progress.rewardProgress?.[i],
      });
    }

    setLevelUpQueue(levelUpQueue);
    setCurrentLevelUpIndex(0);
    setLevelUpOverlayOpened(true);
  };

  const handleApprovedGameGoalsClose = () => {
    if (isPayingGoals) return;

    const paidGoals = approvedGoals;
    dismissApprovedGoals();
    if (!paidGoals || payoutFailed) return;

    setRewardProgress(paidGoals.rewardProgress ?? null);
    setLevelProgress(paidGoals.levelProgress ?? null);
    setStreakProgress(paidGoals.streakProgress ?? null);
    setStreakFreezesAwarded(paidGoals.streakFreezesAwarded ?? 0);

    if (paidGoals.levelProgress?.isUserLeveledUp) {
      handleMultipleLevelUps(paidGoals.levelProgress);
    } else if (paidGoals.streakProgress) {
      setStreakCompletedOverlayOpened(true);
    } else if ((paidGoals.streakFreezesAwarded ?? 0) > 0) {
      setIsStreakFreezeFoundOpen(true);
    } else {
      setRewardUnlockedOverlayOpened(checkIfRewardProgressIsComplete(paidGoals.rewardProgress));
    }
  };

  const isTileOnboardingFlowActive =
    requiresBasicQualificationOnboarding && !isSurveyIntroOpen && !isDynamicProfilerOpen && !activeModal;

  const isSurveyProfileGateActive = isBasicQualificationCompleted && !isProfileSurveyCompleted;
  const isSurveyOnboardingFocusActive = isSurveyProfileGateActive;
  const shouldSuppressSurveyOnboardingHighlight =
    selectedDashboardGameRequest != null ||
    selectedDashboardDealRequest != null ||
    activeModal != null ||
    isSurveyIntroOpen ||
    isDynamicProfilerOpen ||
    isPointsOpen ||
    isStreakOpen ||
    isLevelOpen;

  const surveyPartnerOptions = useMemo(
    () =>
      [
        ...new Set(projects.map((project) => project.partnerName).filter((name): name is string => Boolean(name))),
      ].sort(),
    [projects]
  );

  const sortedProjects = useMemo(() => {
    const visibleProjects = selectedSurveyPartner
      ? projects.filter((project) => project.partnerName === selectedSurveyPartner)
      : projects;

    if (surveySortMode === 'best') {
      return visibleProjects;
    }

    const indexedProjects = visibleProjects.map((projectItem, index) => ({ projectItem, index }));
    const compareOriginalOrder = (left: (typeof indexedProjects)[number], right: (typeof indexedProjects)[number]) =>
      left.index - right.index;
    const getEstimatedTime = (projectItem: SurveyCardDataProps) => projectItem.loiSeconds || Number.MAX_SAFE_INTEGER;

    return [...indexedProjects]
      .sort((left, right) => {
        if (surveySortMode === 'fastest') {
          const timeDifference = getEstimatedTime(left.projectItem) - getEstimatedTime(right.projectItem);
          return timeDifference || compareOriginalOrder(left, right);
        }

        const payoutDifference = right.projectItem.coinValue - left.projectItem.coinValue;
        return payoutDifference || compareOriginalOrder(left, right);
      })
      .map(({ projectItem }) => projectItem);
  }, [projects, selectedSurveyPartner, surveySortMode]);

  const renderSurveysSectionTitle = (className?: string) => (
    <h4 className={classNames('hidden w-full self-start text-left text-content-neutral-default md:block', className)}>
      {t('SURVEYS_SECTION_TITLE')}
    </h4>
  );

  const renderSurveysSectionTitleMobile = (className?: string) => {
    if (!surveysOnly) {
      return (
        <h5
          className={classNames(
            'min-w-0 flex-1 self-start text-left text-content-neutral-default md:hidden',
            className
          )}
        >
          {t('SURVEYS_SECTION_TITLE')}
        </h5>
      );
    }

    return (
      <div className={classNames('flex min-w-0 flex-1 items-center gap-2 self-start md:hidden', className)}>
        <h5 className="min-w-0 flex-1 text-left text-content-neutral-default">{t('SURVEYS_SECTION_TITLE')}</h5>
        <button
          type="button"
          aria-label={tNavigation('SURVEY_TIPS')}
          onClick={() => setIsSurveyTipsOpen(true)}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-indigo-100 bg-white text-indigo-700 shadow-sm transition hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-300"
        >
          <CircleHelp className="h-5 w-5" />
        </button>
      </div>
    );
  };

  const renderSurveyCards = () => (
    <SurveysCarousel
      contentKey={`${surveySortMode}-${selectedSurveyPartner}-${sortedProjects.length}`}
      sectionTitle={renderSurveysSectionTitleMobile()}
      stacked={surveysOnly}
    >
      {sortedProjects.map((item, index) => {
        const isPrimaryOnboardingTile =
          isSurveyOnboardingFocusActive && index === 0 && !shouldSuppressSurveyOnboardingHighlight;
        const isSurveyCardDisabled = requiresRewardSelectionOnboarding || requiresBasicQualificationOnboarding;

        return (
          <SurveyCard
            onClick={() => handleSurveyCardClickSync(item, index)}
            key={index}
            surveyData={item}
            highlighted={isPrimaryOnboardingTile}
            dimBackground={!isSurveyOnboardingFocusActive}
            isGlowing={isPrimaryOnboardingTile}
            state={isSurveyCardDisabled ? SurveyCardState.Disabled : SurveyCardState.Enabled}
          />
        );
      })}
    </SurveysCarousel>
  );

  const renderSurveySortFilters = () => {
    const surveySortFilters = [
      {
        mode: 'fastest' as const,
        label: t('SURVEY_FILTERS.FASTEST_SURVEYS'),
        icon: Clock3,
      },
      {
        mode: 'points' as const,
        label: t('SURVEY_FILTERS.MOST_POINTS'),
        icon: Coins,
      },
      {
        mode: 'best' as const,
        label: t('SURVEY_FILTERS.BEST_SURVEYS'),
        icon: Star,
      },
    ];

    return (
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <p className="text-xs font-semibold uppercase text-slate-400">{t('SURVEY_FILTERS.LABEL')}</p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          {surveyPartnerOptions.length > 1 && (
            <label className="relative">
              <span className="sr-only">Filter surveys by partner</span>
              <select
                value={selectedSurveyPartner}
                onChange={(event) => setSelectedSurveyPartner(event.target.value)}
                className="h-10 w-full appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-8 font-manrope text-xs font-semibold text-slate-600 shadow-sm outline-none transition hover:border-indigo-200 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 sm:w-[170px]"
              >
                <option value="">All partners</option>
                {surveyPartnerOptions.map((partnerName) => (
                  <option key={partnerName} value={partnerName}>
                    {partnerName}
                  </option>
                ))}
              </select>
              <span
                aria-hidden="true"
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
              >
                ⌄
              </span>
            </label>
          )}
          <div
            className="grid grid-cols-3 gap-1 rounded-2xl border border-slate-200 bg-slate-50 p-1 md:flex md:gap-1.5"
            role="radiogroup"
            aria-label={t('SURVEY_FILTERS.ARIA_LABEL')}
          >
            {surveySortFilters.map(({ mode, label, icon }) => {
              const isSelected = surveySortMode === mode;

              return (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => setSurveySortMode(mode)}
                  className={classNames(
                    'flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl px-2 py-2 text-center text-[11px] font-semibold leading-tight transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-300 md:min-w-[150px] md:gap-2 md:px-4 md:text-sm',
                    {
                      'bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-200': isSelected,
                      'text-slate-500 hover:bg-white/75 hover:text-slate-700': !isSelected,
                    }
                  )}
                >
                  {React.createElement(icon, { className: 'h-4 w-4 shrink-0' })}
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderRewardSelectionPrompt = () => {
    if (!requiresRewardSelectionOnboarding) {
      return null;
    }

    return (
      <div className="mb-4 w-full rounded-[28px] border border-[#E8DFF5] bg-[#FBF9FF] p-5 text-center shadow-[0_18px_42px_rgba(76,58,140,0.08)] md:mb-6 md:p-6">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-[20px] bg-[#F7EFE2] text-lg font-semibold text-[#A35A00] shadow-[0_12px_24px_rgba(163,90,0,0.12)]">
          01
        </div>
        <h3 className="mt-4 text-[1.35rem] font-semibold leading-tight text-slate-900">
          {t('REWARD_SELECTION_BLOCKER_TITLE')}
        </h3>
        <p className="mt-3 text-sm leading-6 text-slate-600">{t('REWARD_SELECTION_BLOCKER_DESCRIPTION')}</p>
        <Button
          label={t('SELECT_REWARD')}
          onClick={openRewardSelectionGate}
          state={ButtonState.Enabled}
          type={ButtonType.Filled}
          size={ButtonSize.Big}
          animated={false}
          interactive={false}
          className="mt-5 !h-12 w-full !rounded-2xl !border-0 !bg-[#8B6BFF] !text-white !shadow-[0_18px_34px_rgba(108,77,201,0.22)]"
        />
        <p className="mt-3 text-xs leading-5 text-slate-500">{t('REWARD_SELECTION_BLOCKER_HINT')}</p>
      </div>
    );
  };

  const renderBasicQualificationPrompt = () => {
    if (!requiresBasicQualificationOnboarding) {
      return null;
    }

    return (
      <div className="mb-4 w-full rounded-[28px] border border-[#E8DFF5] bg-[#FBF9FF] p-5 text-center shadow-[0_18px_42px_rgba(76,58,140,0.08)] md:mb-6 md:p-6">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-[20px] bg-[#F7EFE2] text-lg font-semibold text-[#A35A00] shadow-[0_12px_24px_rgba(163,90,0,0.12)]">
          01
        </div>
        <h3 className="mt-4 text-[1.35rem] font-semibold leading-tight text-slate-900">
          {t('QUALIFICATION_BLOCKER_TITLE')}
        </h3>
        <p className="mt-3 text-sm leading-6 text-slate-600">{t('QUALIFICATION_BLOCKER_DESCRIPTION')}</p>
        <Button
          label={t('START_QUALIFICATION_CHECK')}
          onClick={openBasicQualificationGate}
          state={ButtonState.Enabled}
          type={ButtonType.Filled}
          size={ButtonSize.Big}
          animated={false}
          interactive={false}
          className="mt-5 !h-12 w-full !rounded-2xl !border-0 !bg-[#8B6BFF] !text-white !shadow-[0_18px_34px_rgba(108,77,201,0.22)]"
        />
        <p className="mt-3 text-xs leading-5 text-slate-500">{t('QUALIFICATION_BLOCKER_HINT')}</p>
      </div>
    );
  };

  const renderDashboardStatusPage = (content: React.ReactNode, className?: string) => (
    <div className={classNames('flex flex-col flex-1 gap-4', className)}>
      {!!userSettings && !surveysOnly && <DashboardCurrentQuests />}
      {!surveysOnly && <DashboardPwaInstallCard />}
      {content}
    </div>
  );

  const isSurveysSectionLoading = !isStale && (isLoadingProjects || isInitialLoad);
  const isDashboardOffersSectionLoading = !surveysOnly && !isStale && isLoadingDashboardOffers;
  const shouldShowSurveysSection = isSurveysSectionLoading || sortedProjects.length > 0;
  const shouldShowNoProjectsAvailable =
    !isSurveysSectionLoading &&
    !isDashboardOffersSectionLoading &&
    projects.length === 0 &&
    dashboardOffers.length === 0 &&
    userSettings?.profileSurveyCompletion;

  const renderExperienceFeedback = (isVisibleViewport: boolean) =>
    isVisibleViewport && !surveysOnly && accountDetails ? (
      <UserExperienceFeedback
        lifetimePoints={accountDetails.lifetimePointsBalance || 0}
        onRewardAwarded={refreshAccountDetails}
      />
    ) : null;

  const showMyAccountTabWhileOnboarding =
    (!!userSettings?.welcomeModalAcknowledgement && onboardingHighlightState.balanceRewardHighlighted) ||
    isTileOnboardingFlowActive;
  const isSurveyEntryOpen = activeModal === TileClickStatus.Success && !IsUserInProject;
  const isMidProjectIssueOpen = IsUserInProject && !tileClickResponseDto?.isProfileSurvey;
  const isOverQuotaOpen =
    activeModal === TileClickStatus.OverQuota ||
    activeModal === TileClickStatus.ProjectClosed ||
    activeModal === ProjectExitModalType.OverQuota ||
    activeModal === ProfileSurveyExitModalType.OverQuota ||
    activeModal === ProjectExitModalType.OverQuotaNoReview;
  const isNotQualifiedOpen =
    activeModal === TileClickStatus.NotQualified || activeModal === ProjectExitModalType.NotQualified;

  return (
    <>
      <Helmet defer={false}>{isMobile && <meta name="theme-color" content="#0d593b" />}</Helmet>
      <WelcomeAboard />
      <Suspense fallback={null}>
        {/* Modals to show after user clicks on a survey card */}
        {isSurveyEntryOpen && (
          <SurveyEntry
            isOpen
            onClose={() => {
              setActiveModal(null);

              // If the user closes the modal, we want to stop polling
              if (!isProfileSurvey) {
                setIsUserInProject(false);
              }
            }}
            tileClickResponseDto={tileClickResponseDto}
            surveyData={{
              xpValue: selectedTile?.tile.xp || 0,
              rating: selectedTile?.tile.rating || null,
              coinIcon: SurveyCardCoins.CoinBig,
              coinValue: selectedTile?.tile.payoutPoints || 0,
              loiSeconds: selectedTile?.tile.loiSeconds || 0,
              isProfileSurvey: tileClickResponseDto?.isProfileSurvey || false,
              encryptedDetails: selectedTile?.tile.tileDetailsDto || '',
              signature: selectedTile?.signature || '',
            }}
            setIsUserInProject={setIsUserInProject}
            loadProjects={loadProjects}
          />
        )}

        {isMidProjectIssueOpen && (
          <MidProjectIssue
            isOpen
            tileClickResponseDto={tileClickResponseDto}
            onClose={() => {
              setActiveModal(null);
              setIsUserInProject(false);
            }}
          />
        )}

        {activeModal === TileClickStatus.WrongCountry && <Blocker isOpen onClose={() => setActiveModal(null)} />}

        {activeModal === ProjectExitModalType.BadQuality && (
          <BadQuality
            isOpen
            onTryNextSurvey={nextSurvey ? handleTryNextSurvey : undefined}
            levelProgress={levelProgress}
            onClose={() => {
              setActiveModal(null);
            }}
          />
        )}

        {(activeModal === ProjectExitModalType.AyetDelayed || activeModal === ProjectExitModalType.TechnicalIssue) && (
          <AyetSurveyStatus
            isOpen
            status={activeModal === ProjectExitModalType.AyetDelayed ? 'delayed' : 'technical'}
            onClose={() => setActiveModal(null)}
          />
        )}

        {isOverQuotaOpen && (
          <OverQuota
            isOpen
            onTryNextSurvey={nextSurvey ? handleTryNextSurvey : undefined}
            levelProgress={levelProgress}
            onClose={() => {
              setActiveModal(null);

              if (levelProgress?.xpEarned && levelProgress.xpEarned > 0 && hasReview) {
                setUserRatingOverlayOpened(true);
              }
            }}
            tileDetailsDto={tileDetailsDto}
            tileDetailsSignature={tileDetailsSignature}
            isExit={!!projectExitResult || !!profileSurveyExitResult}
            hasReview={activeModal !== ProjectExitModalType.OverQuotaNoReview && hasReview}
          />
        )}

        {isNotQualifiedOpen && (
          <NotQualified
            isOpen
            onTryNextSurvey={nextSurvey ? handleTryNextSurvey : undefined}
            onClose={() => {
              setActiveModal(null);

              if (levelProgress?.xpEarned && levelProgress.xpEarned > 0 && hasReview) {
                setUserRatingOverlayOpened(true);
              }
            }}
            levelProgress={levelProgress}
            tileDetailsDto={tileDetailsDto}
            tileDetailsSignature={tileDetailsSignature}
            isExit={!!projectExitResult || !!profileSurveyExitResult}
            hasReview={hasReview}
          />
        )}
        {userRatingOverlayOpened && (
          <UserRating
            isOpen
            onClose={() => {
              setUserRatingOverlayOpened(false);
              // If the user has just leveled up, we want to show the level up overlay next
              if (levelProgress?.isUserLeveledUp) {
                void handleMultipleLevelUps();
              }
            }}
            tileDetailsDto={tileDetailsDto}
            tileDetailsSignature={tileDetailsSignature}
          />
        )}
        {activeModal === ProjectExitModalType.ProjectCompleted && (
          <ProjectComplete
            isOpen
            onClose={() => {
              // Mark Trustpilot invite to show after project exit
              localStorage.setItem('showTrustpilotModal', 'true');
              setActiveModal(null);
              if (levelProgress?.isUserLeveledUp) {
                void handleMultipleLevelUps();
              } else if (streakProgress) {
                setStreakCompletedOverlayOpened(true);
              } else if (streakFreezesAwarded > 0) {
                setIsStreakFreezeFoundOpen(true);
              } else {
                setRewardUnlockedOverlayOpened(checkIfRewardProgressIsComplete(rewardProgress));
              }
            }}
            levelProgress={levelProgress}
            rewardProgress={rewardProgress}
            tileDetailsDto={tileDetailsDto}
            tileDetailsSignature={tileDetailsSignature}
            isProfileSurvey={isProfileSurvey}
            isPartialReward={isPartialReward}
          />
        )}

        {isSurveyIntroOpen && (
          <SurveyIntro isOpen onClose={handleSurveyIntroCloseSync} onComplete={handleSurveyIntroCompleteSync} />
        )}
        {isDynamicProfilerOpen && (
          <DynamicProfiler
            dynamicProfilerId={dynamicProfilerId}
            isOpen
            onClose={() => {
              if (!isBasicQualificationCompleted) {
                basicQualificationPromptedRef.current = false;
              }

              setIsDynamicProfilerOpen(false);
              setActiveModal(null);
            }}
            onSubmit={handleDynamicProfilerCompletionSync}
            questions={dynamicProfilerQuestions}
            isSkippable={isDynamicProfilerSkippable}
            isDismissible={isDynamicProfilerDismissible}
          />
        )}
        {isSurveyTipsOpen && <SurveyTips isOpen onClose={() => setIsSurveyTipsOpen(false)} />}

        {approvedGoals && (
          <GameGoalsAchieved
            isOpen
            goals={approvedGoals}
            isPaying={isPayingGoals}
            payoutFailed={payoutFailed}
            onClose={handleApprovedGameGoalsClose}
          />
        )}

        {levelUpOverlayOpened && (
          <LevelUp
            key={
              levelUpQueue.length > 0 && currentLevelUpIndex < levelUpQueue.length
                ? `level-up-${levelUpQueue[currentLevelUpIndex].levelReached}-${currentLevelUpIndex}`
                : `level-up-${levelProgress?.currentLevel || 0}`
            }
            isOpen
            onClose={() => {
              // If there are more level-ups in the queue, show the next one
              if (currentLevelUpIndex < levelUpQueue.length - 1) {
                setCurrentLevelUpIndex(currentLevelUpIndex + 1);
              } else {
                setLevelUpOverlayOpened(false);
                setLevelUpQueue([]);
                setCurrentLevelUpIndex(0);
                setIsDynamicProfilerOpen(false);
                setActiveModal(null);

                if (levelProgress?.streakFreezeSlotUnlocked) {
                  setIsStreakFreezeUnlockedOpen(true);
                } else if (streakProgress) {
                  setStreakCompletedOverlayOpened(true);
                } else if (streakFreezesAwarded > 0) {
                  setIsStreakFreezeFoundOpen(true);
                } else {
                  levelProgress?.rewardProgress?.[currentLevelUpIndex] &&
                    setRewardProgress(levelProgress?.rewardProgress?.[currentLevelUpIndex]);
                  setRewardUnlockedOverlayOpened(
                    checkIfRewardProgressIsComplete(levelProgress?.rewardProgress?.[currentLevelUpIndex])
                  );
                }
              }
            }}
            levelReached={
              levelUpQueue.length > 0 && currentLevelUpIndex < levelUpQueue.length
                ? levelUpQueue[currentLevelUpIndex].levelReached
                : levelProgress?.currentLevel || 0
            }
            pointsEarned={
              levelUpQueue.length > 0 && currentLevelUpIndex < levelUpQueue.length
                ? levelUpQueue[currentLevelUpIndex].pointsEarned
                : levelProgress?.pointsEarnedForEachLevelUp?.[currentLevelUpIndex] || 0
            }
            rewardProgress={
              levelUpQueue.length > 0 && currentLevelUpIndex < levelUpQueue.length
                ? levelUpQueue[currentLevelUpIndex].rewardProgress
                : levelProgress?.rewardProgress?.[currentLevelUpIndex]
            }
          />
        )}

        {/* // [DELETE IN PROD-790] */}
        {isNewLevelUpIntroOpen && (
          <NewLevelUpIntro
            isOpen
            onClose={() => {
              setIsNewLevelUpIntroOpen(false);
              setNotifyAboutNewLevelingUpSystem(false);
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              saveUserSettings(UserSettingType.NotifyAboutNewLevelingUpSystem, 'false');
            }}
            pointsEarned={userSettings?.newLevelingUpPointsRewarded || 0}
            rewardProgress={rewardProgress}
          />
        )}
        {/* // [END OF DELETION] */}

        {rewardUnlockedOverlayOpened && (
          <RewardUnlocked
            isOpen
            onClose={() => {
              setRewardUnlockedOverlayOpened(false);
              setActiveModal(null);
            }}
            rewardProgress={rewardProgress}
          />
        )}

        {trustpilotInviteOpen && (
          <TrustpilotInvite
            isOpen
            onClose={() => {
              setTrustpilotInviteOpen(false);
              void refreshUserSettings();
            }}
            inviteUrl={trustpilotInviteUrl || ''}
            inviteId={trustpilotInviteId || 0}
          />
        )}

        {/* // [DELETE IN PROD-792] */}
        {isNewStreakIntroOpen && (
          <NewStreakIntro
            isOpen
            onClose={() => {
              setIsNewStreakIntroOpen(false);
              void refreshUserSettings();
              void refreshAccountDetails();
            }}
            rewardProgress={streakProgress?.rewardProgress}
            startDate={
              streakProgress?.startDate
                ? dayjs.tz(
                    streakProgress.startDate,
                    accountDetails?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
                  )
                : undefined
            }
            completedDays={streakProgress?.bonusCycleDay || 0}
            currentStreak={streakProgress?.currentStreak || 0}
            targetLength={7}
            streakBonusPoints={streakProgress?.streakBonusPoints || 0}
            timezone={accountDetails?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
          />
        )}
        {/* // [END OF DELETION] */}

        {streakCompletedOverlayOpened && (
          <StreakModal
            isOpen
            onClose={() => {
              setStreakCompletedOverlayOpened(false);
              if (streakFreezesAwarded > 0) {
                setIsStreakFreezeFoundOpen(true);
              } else {
                setRewardProgress(streakProgress?.rewardProgress ? streakProgress?.rewardProgress : rewardProgress);
                setRewardUnlockedOverlayOpened(
                  checkIfRewardProgressIsComplete(
                    streakProgress?.rewardProgress ? streakProgress?.rewardProgress : rewardProgress
                  )
                );
              }
            }}
            rewardProgress={streakProgress?.rewardProgress}
            startDate={dayjs(streakProgress?.startDate)}
            completedDays={streakProgress?.bonusCycleDay || 0}
            targetLength={7}
            currentStreak={streakProgress?.currentStreak || 0}
            streakBonusPoints={streakProgress?.streakBonusPoints || 0}
            bestStreak={streakProgress?.bestStreak || 0}
            timezone={accountDetails?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
            previousStreak={streakProgress?.previousStreak || 0}
            previousBestStreak={streakProgress?.previousBestStreak || 0}
            frozenDates={accountDetails?.frozenDates ?? []}
            currentFreezes={accountDetails?.streakFreezeBalance ?? 0}
            maxFreezes={accountDetails?.streakFreezeSlots ?? 0}
            nextFreezeUnlockLevel={accountDetails?.nextStreakFreezeUnlockLevel ?? null}
          />
        )}

        {isStreakFreezeFoundOpen && (
          <StreakFreezeModal
            isOpen
            onClose={() => {
              setIsStreakFreezeFoundOpen(false);
              setStreakFreezesAwarded(0);
              setRewardProgress(streakProgress?.rewardProgress ? streakProgress.rewardProgress : rewardProgress);
              setRewardUnlockedOverlayOpened(
                checkIfRewardProgressIsComplete(
                  streakProgress?.rewardProgress ? streakProgress.rewardProgress : rewardProgress
                )
              );
            }}
            maxFreezes={accountDetails?.streakFreezeSlots ?? 0}
            currentFreezes={accountDetails?.streakFreezeBalance ?? 0}
            freezesEarned={streakFreezesAwarded}
            nextFreezeUnlockLevel={accountDetails?.nextStreakFreezeUnlockLevel ?? null}
          />
        )}

        {isStreakFreezeUnlockedOpen && (
          <StreakFreezeUnlocked
            isOpen
            onClose={() => {
              setIsStreakFreezeUnlockedOpen(false);
              if (streakProgress) {
                setStreakCompletedOverlayOpened(true);
              } else if (streakFreezesAwarded > 0) {
                setIsStreakFreezeFoundOpen(true);
              } else {
                setRewardUnlockedOverlayOpened(checkIfRewardProgressIsComplete(rewardProgress));
              }
            }}
            currentFreezes={accountDetails?.streakFreezeBalance ?? 0}
            maxFreezesAvailable={accountDetails?.streakFreezeSlots ?? 0}
            maxFreezesAllowed={3}
            nextFreezeUnlockLevel={accountDetails?.nextStreakFreezeUnlockLevel ?? null}
            isStreak={(accountDetails?.currentStreak ?? 0) > 0}
            isRewardDay={[2, 4, 7].includes(accountDetails?.bonusCycleDay ?? 0)}
            currentStreak={accountDetails?.currentStreak ?? 0}
            bestStreak={accountDetails?.bestStreak ?? 0}
          />
        )}
      </Suspense>

      {/* Loading overlay for survey clicks */}
      {isSurveyLoading && <div className="fixed inset-0 overlay flex items-center justify-center z-50" />}
      {/* Desktop */}
      <div className="hidden h-screen w-full max-w-full basis-full grow shrink overflow-x-hidden md:flex md:flex-nowrap">
        <div className="h-full w-[18.5rem] shrink-0">
          {!isMobile && (
            <Sidebar
              highlightState={onboardingHighlightState}
              onHighlightStateChange={setOnboardingHighlightState}
              showMyAccountTabWhileOnboarding={showMyAccountTabWhileOnboarding}
              setActiveModal={setActiveModal}
              activeModal={activeModal}
              firstTimeBonus={firstTimeBonus}
              setFirstTimeBonus={setFirstTimeBonus}
              gamification={gamificationTopbarProps}
            />
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col h-full">
          {/* Main content area with white background */}
          <div
            className={classNames(
              'relative flex min-w-0 bg-white flex-1 rounded-tl-3xl overflow-x-hidden overflow-y-auto pb-10 px-6 xl:px-10'
            )}
          >
            {/* Patterned background overlay */}
            <div
              className="pointer-events-none absolute inset-0 -z-10 opacity-50"
              style={{
                backgroundImage:
                  'radial-gradient(60px 60px at 20% 10%, rgba(99,102,241,0.08), transparent 60%), radial-gradient(80px 80px at 80% 0%, rgba(16,185,129,0.08), transparent 60%), radial-gradient(100px 100px at 40% 100%, rgba(2,132,199,0.08), transparent 60%)',
              }}
            />

            {isStale ? (
              renderDashboardStatusPage(
                <>
                  {renderExperienceFeedback(!isMobile)}
                  <StaleProjectsPage onClick={handleRefreshFromIdleTimer} />
                </>,
                '3xl:max-w-[1416px] pt-4'
              )
            ) : shouldShowNoProjectsAvailable ? (
              renderDashboardStatusPage(
                <>
                  {renderExperienceFeedback(!isMobile)}
                  <NoProjectsAvailablePage onClick={handleRefreshFromIdleTimer} />
                </>,
                '3xl:max-w-[1416px] pt-4'
              )
            ) : (
              <div className="flex w-full min-w-0 flex-col flex-1 gap-6 3xl:max-w-[1416px] pt-4">
                {renderRewardSelectionPrompt()}
                {renderBasicQualificationPrompt()}
                {renderExperienceFeedback(!isMobile)}
                {!!userSettings && !surveysOnly && <TodayPlan onProtectStreak={() => setIsStreakOpen(true)} />}
                {!!userSettings && !surveysOnly && <DashboardCurrentQuests />}
                {!surveysOnly && <DashboardPwaInstallCard />}
                {!!userSettings && !surveysOnly && (
                  <div className="flex min-w-0 max-w-full flex-col gap-4">
                    {!isDashboardOffersSectionLoading && <PinnedJourneyCard />}
                    {isDashboardOffersSectionLoading ? (
                      <HomeOffersSkeleton />
                    ) : (
                      <GamesCarousel
                        offers={dashboardOffers}
                        osTypeId={gamesOsTypeId}
                        onOsTypeChange={setGamesOsTypeId}
                        shouldShowPlatformPrompt={shouldShowPlatformPrompt}
                        onOfferClick={handleDashboardOfferClickDuringOnboarding}
                        isPreviewOnly={false}
                      />
                    )}
                  </div>
                )}
                {shouldShowSurveysSection && (
                  <div className="flex w-full min-w-0 flex-col items-start gap-4">
                    {renderSurveysSectionTitle()}
                    {isSurveysSectionLoading ? (
                      <>
                        {renderSurveysSectionTitleMobile('mb-2 md:hidden')}
                        <HomeSurveysSkeleton stacked={surveysOnly} />
                      </>
                    ) : (
                      <>
                        {!isSurveyOnboardingFocusActive && renderSurveySortFilters()}
                        <div className="relative w-full min-w-0 max-w-full pb-6">{renderSurveyCards()}</div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="relative flex h-screen w-full max-w-full basis-full grow shrink flex-col flex-nowrap overflow-x-hidden supports-[height:100dvh]:h-[100dvh] md:hidden">
        {/* Mobile Hero Header - Fixed at top with proper z-index */}
        <GamificationTopbar {...gamificationTopbarProps} />

        {/* Main content area - positioned below hero with proper spacing */}
        <div className="flex flex-col flex-1 bg-white relative z-10 min-h-0">
          {/* Content container with proper spacing and scrollable area */}
          <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto bg-white rounded-t-3xl px-4 pt-4 pb-28 supports-[padding-bottom:env(safe-area-inset-bottom)]:pb-[calc(7rem+env(safe-area-inset-bottom))]">
            {isStale ? (
              renderDashboardStatusPage(
                <>
                  {renderExperienceFeedback(isMobile)}
                  <StaleProjectsPage onClick={handleRefreshFromIdleTimer} />
                </>
              )
            ) : shouldShowNoProjectsAvailable ? (
              renderDashboardStatusPage(
                <>
                  {renderExperienceFeedback(isMobile)}
                  <NoProjectsAvailablePage onClick={handleRefreshFromIdleTimer} />
                </>
              )
            ) : (
              <>
                {renderRewardSelectionPrompt()}
                {renderBasicQualificationPrompt()}
                {renderExperienceFeedback(isMobile)}
                {!!userSettings && !surveysOnly && (
                  <div className="mb-4">
                    <TodayPlan onProtectStreak={() => setIsStreakOpen(true)} />
                  </div>
                )}
                {!!userSettings && !surveysOnly && (
                  <div className="mb-4">
                    <DashboardCurrentQuests />
                  </div>
                )}
                {!surveysOnly && (
                  <div className="mb-4">
                    <DashboardPwaInstallCard />
                  </div>
                )}
                {!!userSettings && !surveysOnly && (
                  <div className="mb-5 flex min-w-0 max-w-full flex-col gap-4">
                    {!isDashboardOffersSectionLoading && <PinnedJourneyCard />}
                    {isDashboardOffersSectionLoading ? (
                      <HomeOffersSkeleton />
                    ) : (
                      <GamesCarousel
                        offers={dashboardOffers}
                        osTypeId={gamesOsTypeId}
                        onOsTypeChange={setGamesOsTypeId}
                        shouldShowPlatformPrompt={shouldShowPlatformPrompt}
                        onOfferClick={handleDashboardOfferClickDuringOnboarding}
                        isPreviewOnly={false}
                      />
                    )}
                  </div>
                )}
                {shouldShowSurveysSection && (
                  <div className="flex w-full min-w-0 flex-col items-start gap-4">
                    {renderSurveysSectionTitle()}
                    {isSurveysSectionLoading ? (
                      <>
                        {renderSurveysSectionTitleMobile('mb-2 md:hidden')}
                        <HomeSurveysSkeleton stacked={surveysOnly} />
                      </>
                    ) : (
                      <>
                        {!isSurveyOnboardingFocusActive && (
                          <div className="mb-4 w-full">{renderSurveySortFilters()}</div>
                        )}
                        <div className="relative w-full min-w-0">{renderSurveyCards()}</div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* TabBar - Fixed at bottom with proper z-index */}
          {/* Keep navigation available during onboarding (mobile has no sidebar). */}
          {!!userSettings && (
            <div className="fixed bottom-0 left-0 right-0 z-[110] bg-white supports-[padding-bottom:env(safe-area-inset-bottom)]:pb-[env(safe-area-inset-bottom)]">
              <TabBar showMyAccountTabWhileOnboarding={showMyAccountTabWhileOnboarding} />
            </div>
          )}
        </div>
      </div>

      <Suspense fallback={null}>
        {selectedDashboardGameRequest && (
          <GameView
            isOpen
            onClose={() => {
              setSelectedDashboardGameRequest(null);
              setSelectedDashboardGame(null);
              setSelectedDashboardGameRank(null);
            }}
            isTopGame={selectedDashboardGameRank != null}
            topGameRank={selectedDashboardGameRank ?? 1}
            rating={selectedDashboardGame?.rating}
            osTypeId={selectedDashboardGame?.osTypeId}
            viewGameRequest={selectedDashboardGameRequest}
            onGameStarted={() => void refreshDashboardGames()}
          />
        )}

        {selectedDashboardDealRequest && (
          <DealView
            isOpen
            onClose={() => {
              setSelectedDashboardDealRequest(null);
              setSelectedDashboardDeal(null);
            }}
            osTypeId={selectedDashboardDeal?.osTypeId}
            viewDealRequest={selectedDashboardDealRequest}
            onDealStarted={() => void refreshDashboardGames()}
          />
        )}

        {/* Gamification Overlays opened via header tiles */}
        {accountDetails && isPointsOpen && (
          <PointsOverlay
            pointsBalance={accountDetails.currentPointsBalance || 0}
            currencyBalance={parseFloat(accountDetails.currencyBalance?.toFixed(2) || '0')}
            lifetimePoints={accountDetails.lifetimePointsBalance || 0}
            rewardTarget={rewardPointsCost}
            reward={
              reward
                ? {
                    name: reward.name,
                    image: reward.imageSrc,
                    type: reward.type,
                    amount: reward.amount,
                    currencySymbol: reward.currencySymbol,
                  }
                : null
            }
            isOpen
            highlighted={false}
            onClose={() => setIsPointsOpen(false)}
            onRewardSelectionOpen={() => navigate('/my-rewards')}
          />
        )}
        {accountDetails && isStreakOpen && (
          <StreakOverlay
            startDate={accountDetails.streakStartDate ? dayjs(accountDetails.streakStartDate) : dayjs()}
            completedDays={accountDetails.bonusCycleDay || 0}
            currentStreak={accountDetails.currentStreak || 0}
            targetLength={7}
            streakBonusPoints={accountDetails.streakBonusPoints || 0}
            bestStreak={accountDetails.bestStreak || 0}
            isOpen
            onClose={() => setIsStreakOpen(false)}
            timezone={accountDetails.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
            frozenDates={accountDetails.frozenDates ?? []}
            currentFreezes={accountDetails.streakFreezeBalance ?? 0}
            maxFreezes={accountDetails.streakFreezeSlots ?? 0}
            nextFreezeUnlockLevel={accountDetails.nextStreakFreezeUnlockLevel ?? null}
          />
        )}
        {accountDetails && isLevelOpen && (
          <LevelOverlay
            currentLevel={accountDetails.currentLevel}
            targetXpForNextLevel={accountDetails.requiredXpForNextLevel}
            nextLevel={accountDetails.currentLevel + 1}
            reward={accountDetails.levelUpPointsReward}
            currentXp={accountDetails.currentXpBalance}
            lifetimeXp={accountDetails.lifetimeXpBalance}
            isOpen
            onClose={() => setIsLevelOpen(false)}
          />
        )}
      </Suspense>
    </>
  );
};

export default Home;
